import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

export default function EditMember() {
    const isMobile = useIsMobile();
    const { slug } = useParams();
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [form, setForm] = useState({ name: '', city: '', country_id: '', state_id: '', bio: '', image_url: '', sharc_id: '' });
    const [hometown, setHometown] = useState({ city: '', country_id: '', state_id: '' });
    const [links, setLinks] = useState([]);
    const [memberId, setMemberId] = useState(null);
    const [memberSlug, setMemberSlug] = useState(slug);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/members/${slug}`),
            axios.get(`${config.API_URL}/picklists`)
        ]).then(([memberRes, pickRes]) => {
            const m = memberRes.data.member;
            const pl = pickRes.data;
            setPicklists(pl);
            setMemberId(m.id);
            setMemberSlug(m.slug);

            // Pre-populate current location — country_id and state_id come directly from DB now
            setForm({
                name: m.name || '',
                city: m.city || '',
                country_id: m.country_id ? String(m.country_id) : '',
                state_id: m.state_id ? String(m.state_id) : '',
                bio: m.bio || '',
                image_url: m.image_url || '',
                sharc_id: m.sharc_id || '',
            });

            // Pre-populate hometown — stored as name strings, match back to IDs
            const matchedCountry = m.hometown_country
                ? pl.countries.find(c => c.name === m.hometown_country)
                : null;
            const countryId = matchedCountry ? String(matchedCountry.id) : '';
            const matchedState = m.hometown_state && matchedCountry
                ? pl.states_regions.find(s => s.name === m.hometown_state && s.country_id === matchedCountry.id)
                : null;

            setHometown({
                city: m.hometown_city || '',
                country_id: countryId,
                state_id: matchedState ? String(matchedState.id) : '',
            });

            setLinks(memberRes.data.links || []);
        })
        .catch(() => setError('Failed to load member.'))
        .finally(() => setLoading(false));
    }, [slug]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'country_id' ? { state_id: '' } : {}) }));
    };

    const filteredStates = picklists && form.country_id
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const updateHometown = (field, value) =>
        setHometown(prev => ({ ...prev, [field]: value, ...(field === 'country_id' ? { state_id: '' } : {}) }));

    const hometownStates = picklists && hometown.country_id
        ? picklists.states_regions.filter(s => s.country_id === parseInt(hometown.country_id))
        : [];

    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (i, field, value) => setLinks(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
    const removeLink = (i) => setLinks(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Member name is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            await axios.put(`${config.API_URL}/members/${memberId}`, {
                name: form.name.trim(),
                city: form.city.trim() || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
                bio: form.bio.trim() || null,
                image_url: form.image_url.trim() || null,
                sharc_id: form.sharc_id.trim() || null,
                hometown_city: hometown.city.trim() || null,
                hometown_country_id: hometown.country_id ? parseInt(hometown.country_id) : null,
                hometown_state_id: hometown.state_id ? parseInt(hometown.state_id) : null,
                links: links.filter(l => l.description.trim() && l.url.trim()),
            }, { headers: { Authorization: idToken } });
            navigate(`/members/${memberSlug}`);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save member.');
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to={`/members/${memberSlug}`} style={styles.backLink}>← Back to Profile</Link>
            <h1 style={styles.title}>Edit Member</h1>
            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.form}>
                <div style={styles.sectionHeader}>Basic Info</div>

                <div style={styles.field}>
                    <label style={styles.label}>Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} style={styles.input} />
                </div>
                <div style={styles.field}>
                    <label style={styles.label}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="Current city" style={styles.input} />
                </div>
                <div style={styles.field}>
                    <label style={styles.label}>Country</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select country...</option>
                        {picklists && picklists.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                {form.country_id && filteredStates.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>State / Region</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select state/region...</option>
                            {filteredStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                )}

                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Bio &amp; Image</div>

                <div style={styles.field}>
                    <label style={styles.label}>Bio</label>
                    <textarea name="bio" value={form.bio} onChange={handleChange} rows={4} placeholder="A short biography..." style={{ ...styles.input, resize: 'vertical', lineHeight: '1.6' }} />
                </div>
                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." style={styles.input} />
                    {form.image_url && <div style={{ marginTop: '10px' }}><img src={form.image_url} alt="Preview" style={styles.previewImg} onError={e => { e.target.style.display = 'none'; }} /></div>}
                </div>

                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Hometown</div>
                <div style={styles.sectionHint}>Where this member is originally from.</div>

                <div style={styles.repeatCard}>
                    <div style={isMobile ? { ...styles.repeatCardRow, ...Smobile.repeatCardRow } : styles.repeatCardRow}>
                        <div style={{ flex: 1 }}>
                            <label style={styles.miniLabel}>City</label>
                            <input value={hometown.city} onChange={e => updateHometown('city', e.target.value)} placeholder="City..." style={styles.input} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={styles.miniLabel}>Country</label>
                            <select value={hometown.country_id} onChange={e => updateHometown('country_id', e.target.value)} style={styles.input}>
                                <option value="">Select...</option>
                                {picklists && picklists.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        {hometown.country_id && hometownStates.length > 0 && (
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>State / Region</label>
                                <select value={hometown.state_id} onChange={e => updateHometown('state_id', e.target.value)} style={styles.input}>
                                    <option value="">Select...</option>
                                    {hometownStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Links</div>
                <div style={styles.sectionHint}>Website, Bandcamp, Spotify, social media, etc.</div>

                {links.map((link, i) => (
                    <div key={i} style={styles.repeatCard}>
                        <div style={isMobile ? { ...styles.repeatCardRow, ...Smobile.repeatCardRow } : styles.repeatCardRow}>
                            <div style={isMobile ? { width: '100%' } : { flex: '0 0 160px' }}>
                                <label style={styles.miniLabel}>Label</label>
                                <input value={link.description} onChange={e => updateLink(i, 'description', e.target.value)} placeholder="e.g. Bandcamp" style={styles.input} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>URL</label>
                                <input value={link.url} onChange={e => updateLink(i, 'url', e.target.value)} placeholder="https://..." style={styles.input} />
                            </div>
                            <button onClick={() => removeLink(i)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                        </div>
                    </div>
                ))}
                <button onClick={addLink} style={styles.addRowButton}>+ Add Link</button>

                <div style={{ ...styles.sectionDivider, marginBottom: '24px' }} />
                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '680px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
    sectionDivider: { borderTop: '1px solid #2a2a2a', margin: '28px 0 24px' },
    sectionHint: { fontSize: '13px', color: '#666', marginBottom: '14px', marginTop: '-10px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', color: '#aaa', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    miniLabel: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    previewImg: { width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333' },
    repeatCard: { backgroundColor: '#252525', border: '1px solid #333', borderRadius: '6px', padding: '14px', marginBottom: '10px' },
    repeatCardRow: { display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' },
    addRowButton: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px dashed #555', borderRadius: '4px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' },
    removeButton: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '8px 10px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    errorBox: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.7) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
//
// Found via the flex-row repeater pattern (not gridTemplateColumns) that the
// original grep-based audit missed for this file — same pattern as
// AddArtist/EditArtist and AddVenue/EditVenue. The Links row's fixed
// flex: '0 0 160px' label column squeezed the URL field to ~100px on mobile.
const Smobile = {
    repeatCardRow: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
    },
};
